import React, { useEffect, useState, useCallback } from "react";
import { Layers, ClipboardCheck, Clock, CheckCircle2, Download, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, openFileById } from "@/lib/api";
import OfficerShell from "./OfficerShell";
import { RdcMarkingTab } from "./RdcDashboard";

export const PRELIM_Q = [
  "जोडलेल्या कार्यपद्धतीनुसार अनिवार्य अटींनुसार किमान उच्च माध्यमिक शालांत परीक्षा (HSC) उत्तीर्ण आहे काय?",
  "अर्जदार यांच्या वयाची किमान वय १८ वर्ष पूर्ण आहे काय?",
  "अर्जदार यांना संगणकाचे मूलभूत ज्ञान आहे काय?",
  "अर्जदार संबंधित ग्रामीण/स्थानिक स्वराज्य संस्थेचा रहिवासी आहे काय?",
  "अर्जदारासाठी स्वतःचे आधारकार्ड व पॅनकार्ड जोडलेले आहे काय?",
  "नैतिक अध:पतन, आर्थिक फसवणूक किंवा सायबर गुन्ह्याबाबत दोषी नसल्याबाबत पोलिस चारित्र्य पडताळणी जोडली आहे काय?",
];

// स्थानिक स्वराज्य संस्था + वॉर्ड (वॉर्ड असलेल्या संस्थांसाठी) — पत्ता तपासणीसाठी
export function WardInfo({ d }) {
  if (!d?.lbWard) return null;
  return (
    <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-left text-[12.5px]" data-testid="prelim-ward-info">
      <p className="font-bold text-amber-800">वॉर्ड / प्रभाग: <span data-testid="prelim-ward-name">{d.lbWard}</span></p>
      <p className="text-slate-600">अर्जदाराचा पत्ता: <span data-testid="prelim-ward-address">{d.address || "—"}</span></p>
    </div>
  );
}

// प्रत्येक प्रश्नासमोर दाखवायची संबंधित अपलोड केलेली कागदपत्रे (data नुसार)
export function docsForQ(i, d) {
  if (i === 0) {
    const out = [{ label: "HSC प्रमाणपत्र", file: d.edu_file_0 }];
    if (d.name_change_file?.fileId) out.push({ label: "नावात बदल कागदपत्र (गॅझेट / विवाह दाखला)", file: d.name_change_file });
    return out;
  }
  if (i === 1) return [{ label: "आधारकार्ड (वय पुरावा)", file: d.aadhaar_file }];
  if (i === 2) {
    const defs = ["MS-CIT प्रमाणपत्र", "सायबर सुरक्षा प्रमाणपत्र", "संगणक हाताळणी प्रमाणपत्र"];
    const out = [];
    for (let k = 0; k < 3; k++) {
      const f = d[`cl_file_${k}`];
      if (f && f.fileId) out.push({ label: d[`cl_name_${k}`] || defs[k], file: f });
    }
    return out.length ? out : defs.map((lbl, k) => ({ label: lbl, file: d[`cl_file_${k}`] }));
  }
  if (i === 3) return [{ label: "रहिवासी पुरावा १", file: d.residence_proof_file }, { label: "रहिवासी पुरावा २", file: d.residence_proof_file_2 }, { label: "आधारकार्ड", file: d.aadhaar_file }];
  if (i === 4) return [{ label: "आधारकार्ड", file: d.aadhaar_file }, { label: "पॅनकार्ड", file: d.pan_file }];
  if (i === 5) return [{ label: "पोलिस चारित्र्य पडताळणी", file: d.cv_file }];
  return [];
}

// Read-only mirror of the tehsildar's प्राथमिक पडताळणी नमुना — used in RDC (and SDO) views
export function PrelimMirror({ app }) {
  const d = app?.data || {};
  const pv = app?.prelimVerification || {};
  const q = pv.questions || {};
  const fileLink = (f) => f?.fileId
    ? <button type="button" onClick={() => openFileById(f.fileId)} className="text-[12px] text-[#c2570a] underline">पहा</button>
    : <span className="text-[12px] text-slate-300">—</span>;
  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="prelim-mirror">
      <p className="mb-1 text-center text-[15px] font-bold text-[#1b5e3f]">प्राथमिक पडताळणी नमुना (तहसीलदार)</p>
      <p className="mb-3 text-center text-[12.5px] text-slate-500">अर्ज क्र. {app.applicationNumber} · {d.nameMr || app.applicantName} · {d.village || d.city || ""}</p>
      {!pv.decision ? (
        <p className="rounded-lg bg-amber-50 p-3 text-center text-[13px] text-amber-700">तहसीलदार यांची प्राथमिक पडताळणी अद्याप झालेली नाही.</p>
      ) : (
        <>
          <div className={`mb-4 rounded-xl p-3 text-center text-[14px] font-bold ${pv.decision === "अपात्र" ? "bg-red-50 text-red-700 ring-1 ring-red-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}>
            तहसीलदार अभिप्राय: {pv.decision}{pv.by ? ` · ${pv.by}` : ""}
            {pv.remark && <div className="mt-1 text-[12.5px] font-normal">तहसीलदार शेरा: {pv.remark}</div>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="bg-slate-100 text-left text-slate-600"><th className="p-2">अ.क्र.</th><th className="p-2">मुद्दा</th><th className="p-2">दस्तऐवज</th><th className="p-2 text-center">पडताळणी</th></tr></thead>
              <tbody>
                {PRELIM_Q.map((label, i) => {
                  const ans = q[`q${i + 1}`];
                  return (
                    <tr key={i} className="border-b border-slate-100 align-top">
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2 text-slate-700">{label}</td>
                      <td className="p-2">{docsForQ(i, d).map((doc, k) => (
                        <div key={k} className="flex items-center gap-1.5"><span className="text-[11.5px] text-slate-500">{doc.label}</span>{fileLink(doc.file)}</div>
                      ))}</td>
                      <td className="p-2 text-center"><span className={ans === "होय" ? "font-semibold text-emerald-700" : ans === "नाही" ? "font-semibold text-red-600" : "text-slate-400"}>{ans || "—"}</span>
                        {(pv.remarks || {})[`q${i + 1}`] && <div className="mt-1 text-[11.5px] text-slate-500">शेरा: {(pv.remarks || {})[`q${i + 1}`]}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {app.sdoRemark && <div className="mt-4 rounded-xl bg-blue-50 p-3 text-[13px] text-blue-800 ring-1 ring-blue-200"><b>SDO शेरा:</b> {app.sdoRemark}</div>}
        </>
      )}
    </div>
  );
}


export function FileChip({ file, label }) {
  const open = () => openFileById(file.fileId);
  if (!file || !file.fileId) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1.5 text-[12.5px] font-medium text-red-500 ring-1 ring-red-200">
        {label}: जोडलेले नाही
      </span>
    );
  }
  return (
    <button type="button" onClick={open} data-testid={`prelim-doc-${file.fileId}`}
      className="inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f]/8 px-3 py-1.5 text-[12.5px] font-semibold text-[#1b5e3f] ring-1 ring-[#1b5e3f]/25 transition-colors hover:bg-[#1b5e3f]/15">
      <Download className="h-3.5 w-3.5" /> {label} पहा
    </button>
  );
}

function PrelimForm({ app, onDone }) {
  const d = app.data || {};
  const [ans, setAns] = useState({ q1: "", q2: "", q3: "", q4: "", q5: "", q6: "" });
  const [rem, setRem] = useState({ q1: "", q2: "", q3: "", q4: "", q5: "", q6: "" });
  const [decision, setDecision] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const allYes = [1, 2, 3, 4, 5, 6].every((i) => ans[`q${i}`] === "होय");
  const anyNo = [1, 2, 3, 4, 5, 6].some((i) => ans[`q${i}`] === "नाही");
  useEffect(() => {
    if (allYes) setDecision("पात्र");
  }, [allYes]);

  const submit = async () => {
    const unanswered = [1, 2, 3, 4, 5, 6].some((i) => !ans[`q${i}`]);
    if (unanswered) { setErr("कृपया सर्व ६ प्रश्नांची होय/नाही उत्तरे द्या."); return; }
    const anyNo = [1, 2, 3, 4, 5, 6].some((i) => ans[`q${i}`] === "नाही");
    if (anyNo && !remark.trim()) { setErr("एखादे उत्तर 'नाही' असल्यास शेरा (टीप) आवश्यक आहे."); return; }
    if (!decision) { setErr("कृपया पात्र / अपात्र अभिप्राय निवडा."); return; }
    setBusy(true); setErr("");
    try {
      const rbody = { r1: rem.q1, r2: rem.q2, r3: rem.q3, r4: rem.q4, r5: rem.q5, r6: rem.q6 };
      await api.officerPrelim(app.id, { ...ans, ...rbody, decision, remark });
      toast.success(`अर्ज क्र. ${app.applicationNumber} ची प्राथमिक पडताळणी जतन झाली (${decision}).`);
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E0A106]/50 bg-white p-5" data-testid="prelim-form">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#1b5e3f]/5 px-4 py-3">
        <div>
          <p className="text-[12.5px] text-slate-500">अर्ज क्रमांक</p>
          <p className="font-latin text-[20px] font-bold text-[#1b3d2a]" data-testid="prelim-app-number">{app.applicationNumber}</p>
        </div>
        <div className="text-right">
          <p className="text-[15px] font-bold text-[#1b3d2a]">{d.nameMr || app.applicantName}</p>
          <p className="text-[12.5px] text-slate-500">{d.village || d.city || "—"} · {d.orgType || "नवीन आपले सरकार सेवा केंद्र"}</p>
          {app.sentBackForPrelim && <p className="mt-1 text-[12px] font-semibold text-red-600" data-testid="prelim-sent-back-note">RDC ने परत पाठवले: {app.sentBackReason}</p>}
        </div>
      </div>
      <WardInfo d={d} />

      <div className="space-y-3">
        {PRELIM_Q.map((q, i) => {
          const key = `q${i + 1}`;
          return (
            <div key={key} className="rounded-lg border border-slate-200 p-3.5" data-testid={`prelim-q-${i + 1}`}>
              <p className="text-[13.5px] font-medium text-slate-700">{i + 1}. {q}</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {docsForQ(i, d).map((doc, k) => (
                  <FileChip key={k} file={doc.file} label={doc.label} />
                ))}
              </div>
              <div className="mt-3 flex gap-2 border-t border-dashed border-slate-200 pt-3">
                {["होय", "नाही"].map((o) => (
                  <button key={o} type="button" onClick={() => setAns({ ...ans, [key]: o })}
                    data-testid={`prelim-q${i + 1}-${o === "होय" ? "yes" : "no"}`}
                    className={`rounded-full px-5 py-1.5 text-[13px] font-semibold transition-colors ${ans[key] === o ? (o === "होय" ? "bg-[#5a9e3a] text-white" : "bg-red-500 text-white") : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{o}</button>
                ))}
              </div>
              <textarea value={rem[key]} onChange={(e) => setRem({ ...rem, [key]: e.target.value })} rows={1}
                placeholder="या मुद्द्याबाबत शेरा / टीप (ऐच्छिक)" data-testid={`prelim-q${i + 1}-remark`}
                className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f]" />
            </div>
          );
        })}
      </div>

      <div className="mt-5">
        <p className="mb-2 text-[14px] font-semibold text-[#1b5e3f]">तहसीलदार यांचा अभिप्राय</p>
        <div className="mb-3 flex gap-2">
          {["पात्र", "अपात्र"].map((o) => (
            <button key={o} type="button" onClick={() => setDecision(o)} disabled={o === "अपात्र" && allYes} data-testid={`prelim-decision-${o === "पात्र" ? "eligible" : "ineligible"}`}
              className={`rounded-full px-6 py-2 text-[14px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${decision === o ? (o === "पात्र" ? "bg-[#5a9e3a] text-white" : "bg-red-600 text-white") : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>{o}</button>
          ))}
        </div>
        {allYes && <p className="mb-3 -mt-1 text-[12.5px] text-emerald-700">सर्व ६ उत्तरे "होय" असल्याने अभिप्राय आपोआप <strong>पात्र</strong> निवडला आहे.</p>}
        <label className="mb-1.5 block text-[13px] font-semibold text-slate-600">शेरा / टीप {anyNo ? <span className="text-red-500">* (आवश्यक — कारण एखादे उत्तर "नाही" आहे)</span> : <span className="text-slate-400">(ऐच्छिक)</span>}</label>
        <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} placeholder={anyNo ? "अपात्रतेचे / नाही चे कारण नमूद करा" : "शेरा / टीप (ऐच्छिक)"} data-testid="prelim-remark"
          className={`w-full rounded-lg border px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f] ${anyNo && !remark.trim() ? "border-red-300" : "border-slate-300"}`} />
      </div>
      {err && <p className="mt-2 text-[13px] text-red-600" data-testid="prelim-error">{err}</p>}
      <button disabled={busy} onClick={submit} data-testid="prelim-submit-btn"
        className="mt-4 w-full rounded-full bg-[#1b5e3f] px-6 py-3 text-[14.5px] font-semibold text-white transition-colors hover:bg-[#144d33] disabled:opacity-60">
        {busy ? "जतन होत आहे..." : "प्राथमिक पडताळणी जतन करा व पुढील अर्ज"}
      </button>
    </div>
  );
}

// ---- मंडळ अधिकारी अहवाल खात्री (तहसीलदार) ----
function ConfirmList() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);       // selected app
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setApps(await api.tehsildarConfirmList() || []); } catch (e) { /* noop */ }
    setLoading(false); setOpen(null); setRemark("");
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    if (action === "sendback" && !remark.trim()) { toast.error("परत पाठवण्यासाठी कारण (शेरा) लिहा."); return; }
    setBusy(true);
    try {
      await api.tehsildarConfirm(open.id, { action, remark });
      toast.success(action === "confirm" ? "अहवालास खात्री देण्यात आली." : "अहवाल पुनर्पडताळणीसाठी परत पाठवला.");
      load();
    } catch (err) { toast.error("त्रुटी: " + (err.message || "")); }
    setBusy(false);
  };

  if (open) {
    const r = open.visitReport || {};
    const d = open.data || {};
    const photo = (p) => p?.fileId ? (
      <button type="button" onClick={() => openFileById(p.fileId)} className="text-[12px] text-[#c2570a] underline">फोटो पहा</button>
    ) : <span className="text-slate-300">—</span>;
    return (
      <div>
        <button onClick={() => setOpen(null)} className="mb-3 rounded-full border border-slate-300 px-4 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50" data-testid="teh-confirm-back">← यादीकडे परत</button>
        <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="teh-confirm-detail">
          <p className="mb-1 text-center text-[15px] font-bold text-[#1b5e3f]">मंडळ अधिकारी — पायाभूत सुविधा तपासणी अहवाल</p>
          <p className="mb-3 text-center text-[12.5px] text-slate-500">अर्ज क्र. {open.applicationNumber} · {d.nameMr || open.applicantName} · {d.village || d.city || ""}</p>

          <div className={`mb-4 rounded-xl p-3 text-center text-[14px] font-bold ${r.recommendation === "बदल" ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}>
            एकंदर अभिप्राय: {r.recommendation === "बदल" ? "अर्जदाराने दिलेल्या माहितीमध्ये बदल आहे" : "अर्जदाराने दिलेली माहिती योग्य आहे"}
            {r.remark && <div className="mt-1 text-[12.5px] font-normal">शेरा: {r.remark}</div>}
          </div>

          <div className="mb-4 grid gap-x-8 gap-y-1 rounded-xl bg-[#faf8f0] p-3 md:grid-cols-2">
            {[["पडताळणी अधिकारी", r.officerName], ["पदनाम", r.designation], ["स्थळ पाहणी दिनांक", r.inspectionDate], ["वेळ", r.inspectionTime]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1">
                <span className="text-[12.5px] text-slate-500">{k}</span>
                <span className="text-[13px] font-medium text-slate-800">{v || "—"}</span>
              </div>
            ))}
            <div className="flex justify-between gap-3 py-1"><span className="text-[12.5px] text-slate-500">बाहेरील फोटो</span>{photo(r.photoOutside)}</div>
            <div className="flex justify-between gap-3 py-1"><span className="text-[12.5px] text-slate-500">आतील फोटो</span>{photo(r.photoInside)}</div>
          </div>

          {Array.isArray(r.checklist) && r.checklist.length > 0 && (
            <div className="mb-4 overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="bg-slate-100 text-left text-slate-600"><th className="p-2">अ.क्र.</th><th className="p-2">मुद्दा</th><th className="p-2">अर्जदाराने नमूद केलेला तपशील</th><th className="p-2 text-center">प्रत्यक्ष पडताळणी</th></tr></thead>
                <tbody>
                  {r.checklist.map((c) => (
                    <tr key={c.n} className="border-b border-slate-100 align-top">
                      <td className="p-2">{c.n}</td>
                      <td className="p-2">{c.label}</td>
                      <td className="p-2 text-slate-600">{c.applicantDetail || "—"}</td>
                      <td className="p-2 text-center"><span className={!c.verify ? "text-slate-400" : c.verify === "नाही" ? "font-semibold text-red-600" : "font-semibold text-emerald-700"}>{c.verify || "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} placeholder="तहसीलदार शेरा (परत पाठवत असल्यास आवश्यक)"
            className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" data-testid="teh-confirm-remark" />
          <div className="flex flex-wrap gap-3">
            <button onClick={() => act("confirm")} disabled={busy} data-testid="teh-confirm-btn"
              className="flex-1 rounded-full bg-[#1b5e3f] py-3 text-[14px] font-bold text-white hover:bg-[#144d33] disabled:opacity-60">खात्री करा ✓</button>
            <button onClick={() => act("sendback")} disabled={busy} data-testid="teh-sendback-btn"
              className="flex-1 rounded-full border border-amber-500 py-3 text-[14px] font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-60">पुनर्पडताळणीसाठी परत पाठवा</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
      <p className="mb-3 text-[16px] font-bold text-[#1b5e3f]">मंडळ अधिकारी पायाभूत सुविधा तपासणी अहवाल — खात्रीसाठी</p>
      {loading ? (
        <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
      ) : apps.length === 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="teh-confirm-empty">
          <PartyPopper className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
          <p className="text-[14px] font-semibold text-emerald-700">खात्रीसाठी प्रलंबीत अहवाल नाहीत.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="teh-confirm-table">
            <thead>
              <tr className="bg-[#1b5e3f] text-left text-white">
                <th className="p-2.5 font-semibold">अ.क्र.</th>
                <th className="p-2.5 font-semibold">अर्ज क्रमांक</th>
                <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्था</th>
                <th className="p-2.5 font-semibold">मंडळ अधिकारी अभिप्राय</th>
                <th className="p-2.5 text-center font-semibold">पहा / खात्री</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a, i) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                  <td className="p-2.5">{i + 1}</td>
                  <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                  <td className="p-2.5">{a.data?.nameMr || a.applicantName}</td>
                  <td className="p-2.5">{a.data?.village || a.data?.city || "—"}</td>
                  <td className="p-2.5"><span className={a.visitDecision === "बदल" ? "font-semibold text-amber-700" : "font-semibold text-emerald-700"}>{a.visitDecision === "बदल" ? "बदल आहे" : "योग्य"}</span></td>
                  <td className="p-2.5 text-center">
                    <button onClick={() => setOpen(a)} data-testid={`teh-confirm-open-${a.id}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#c2570a] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90">पहा</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function TehsildarDashboard() {
  const [tab, setTab] = useState("dash");
  const [rows, setRows] = useState([]);
  const [pstats, setPstats] = useState({ total: 0, done: 0, pending: 0 });
  const [current, setCurrent] = useState(null);   // full app detail for oldest pending
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lb, ps, list] = await Promise.all([
        api.officerLocalBodyStats(), api.officerPrelimStats(), api.officerList("", "", ""),
      ]);
      setRows(lb); setPstats(ps);
      const pending = list
        .filter((a) => !a.prelimDone)
        .sort((a, b) => new Date(a.submittedAt || a.createdAt || 0) - new Date(b.submittedAt || b.createdAt || 0));
      if (pending.length > 0) {
        const full = await api.officerDetail(pending[0].id);
        setCurrent(full);
      } else {
        setCurrent(null);
      }
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} data-testid={`tehsildar-tab-${key}`}
      className={`rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${tab === key ? "bg-[#1b5e3f] text-white" : "bg-white text-[#1b5e3f] ring-1 ring-[#1b5e3f]/25 hover:bg-[#1b5e3f]/5"}`}>
      {label}
    </button>
  );

  return (
    <OfficerShell>
      <div className="mb-5 flex flex-wrap gap-2">
        {tabBtn("dash", "डॅशबोर्ड")}
        {tabBtn("prelim", "प्राथमिक पडताळणी")}
        {tabBtn("confirm", "मंडळ अहवाल खात्री")}
        {tabBtn("marking", "गुणांकन")}
      </div>

      {tab === "dash" && (
        <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
          <p className="mb-3 text-[16px] font-bold text-[#1b5e3f]">स्थानिक स्वराज्य संस्थानिहाय अर्ज स्थिती</p>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्थेचे नाव</th>
                  <th className="p-2.5 text-center font-semibold">प्रारूप अर्ज</th>
                  <th className="p-2.5 text-center font-semibold">एकूण प्राप्त अर्ज</th>
                  <th className="p-2.5 text-center font-semibold">प्राथमिक पडताळणी झालेले (पात्र/अपात्र)</th>
                  <th className="p-2.5 text-center font-semibold">प्राथमिक पडताळणीसाठी प्रलंबीत</th>
                  <th className="p-2.5 text-center font-semibold">प्रत्यक्ष भेटीद्वारे पडताळणी झालेले</th>
                  <th className="p-2.5 text-center font-semibold">प्रत्यक्ष भेट पडताळणीसाठी प्रलंबीत</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">कोणतेही अर्ज नाहीत</td></tr>}
                {rows.map((r, i) => (
                  <tr key={r.localBody} className="border-b border-slate-100">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">{r.localBody}</td>
                    <td className="p-2.5 text-center font-semibold text-slate-500">{r.draft || 0}</td>
                    <td className="p-2.5 text-center font-bold">{r.total}</td>
                    <td className="p-2.5 text-center">
                      <span className="font-semibold text-emerald-700">{r.prelimEligible}</span>
                      <span className="text-slate-400"> / </span>
                      <span className="font-semibold text-red-600">{r.prelimIneligible}</span>
                    </td>
                    <td className="p-2.5 text-center font-semibold text-amber-700">{r.prelimPending}</td>
                    <td className="p-2.5 text-center">{r.visitDone}</td>
                    <td className="p-2.5 text-center font-semibold text-amber-700">{r.visitPending}</td>
                  </tr>
                ))}
                {rows.length > 0 && (() => {
                  const s = rows.reduce((a, r) => ({ total: a.total + (r.total || 0), pe: a.pe + (r.prelimEligible || 0), pi: a.pi + (r.prelimIneligible || 0), pp: a.pp + (r.prelimPending || 0), vd: a.vd + (r.visitDone || 0), vp: a.vp + (r.visitPending || 0), draft: a.draft + (r.draft || 0) }), { total: 0, pe: 0, pi: 0, pp: 0, vd: 0, vp: 0, draft: 0 });
                  return (
                    <tr className="border-t-2 border-[#1b5e3f] bg-[#f3efe2] font-bold" data-testid="dashboard-total-row">
                      <td className="p-2.5"></td>
                      <td className="p-2.5 text-[#1b5e3f]">एकूण</td>
                      <td className="p-2.5 text-center text-slate-500">{s.draft}</td>
                      <td className="p-2.5 text-center">{s.total}</td>
                      <td className="p-2.5 text-center"><span className="text-emerald-700">{s.pe}</span><span className="text-slate-400"> / </span><span className="text-red-600">{s.pi}</span></td>
                      <td className="p-2.5 text-center text-amber-700">{s.pp}</td>
                      <td className="p-2.5 text-center">{s.vd}</td>
                      <td className="p-2.5 text-center text-amber-700">{s.vp}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-[12.5px] text-slate-400">पात्र = हिरवा, अपात्र = लाल. आपल्या तहसीलमधील सर्व अर्जांची स्थिती संस्थानिहाय दाखवली आहे.</p>
        </div>
      )}

      {tab === "prelim" && (
        <div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { label: "एकूण अर्ज", val: pstats.total, Icon: Layers, cls: "text-[#1b5e3f] bg-emerald-50" },
              { label: "प्राथमिक पडताळणी झालेले", val: pstats.done, Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50" },
              { label: "प्राथमिक पडताळणीसाठी प्रलंबीत", val: pstats.pending, Icon: Clock, cls: "text-amber-700 bg-amber-50" },
            ].map((c) => (
              <div key={c.label} className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
                <div className={`mb-2 flex h-10 w-10 items-center justify-center rounded-xl ${c.cls}`}><c.Icon className="h-5 w-5" /></div>
                <p className="text-[28px] font-bold leading-none text-[#1b3d2a] font-latin">{c.val}</p>
                <p className="mt-1 text-[13px] text-slate-500">{c.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-5">
            <p className="mb-3 flex items-center gap-2 text-[16px] font-bold text-[#1b5e3f]">
              <ClipboardCheck className="h-5 w-5" /> प्राथमिक पडताळणी
              {pstats.pending > 0 && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[12px] font-semibold text-amber-800">प्रलंबीत: {pstats.pending}</span>}
            </p>

            {loading ? (
              <div className="rounded-2xl border border-[#e6dcc4] bg-white p-10 text-center text-slate-400">लोड होत आहे...</div>
            ) : current ? (
              <>
                <p className="mb-3 text-[12.5px] text-slate-500">सर्वात जुना प्रलंबीत अर्ज खाली दाखवला आहे. हा अर्ज जतन केल्यानंतरच पुढील अर्ज उघडेल.</p>
                <PrelimForm app={current} onDone={load} />
              </>
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="prelim-all-done">
                <PartyPopper className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
                <p className="text-[16px] font-bold text-emerald-800">सर्व अर्जांची प्राथमिक पडताळणी पूर्ण झाली आहे ✓</p>
                <p className="mt-1 text-[13.5px] text-emerald-700">सध्या प्रलंबीत कोणताही अर्ज नाही.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "confirm" && <ConfirmList />}
      {tab === "marking" && <RdcMarkingTab role="tehsildar" />}
    </OfficerShell>
  );
}
